import { useState } from "react"
import {
	Alert,
	Badge,
	Box,
	BoxOwnProps,
	Divider,
	Heading,
	Icons,
	Inline,
	Money,
	Select,
	Spinner,
	Stack,
	TabContent,
	TabItem,
	Table,
	Tabs,
	TabsList,
	Text,
	Time,
	Tooltip,
	useBreakpoints,
} from "@sembark-travel/ui-base"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import {
	CopyToClipboard,
	CopyToClipboardButton,
} from "@sembark-travel/ui-copy-to-clipboard"
import {
	formatDate,
	getDiff,
	localOrUtcTimestampToLocalDate,
} from "@sembark-travel/datetime-utils"
import { Link, useNavigateToBack } from "@sembark-travel/ui-router"
import { generatePath } from "../router-utils"
import {
	TOtaBookingDetail,
	TOtaBookingExtraService,
	TOtaBookingPayment,
	TOtaBookingRoom,
	TOtaBookingVersionWithRooms,
	useHotelOtaBooking,
	useHotelOtaBookingVersions,
} from "./store"
import { OtaBookingChildrenCount, RetryOtaBookingSync } from "./List"
import paidStamp from "../images/fully_paid_stamp.svg"
import cancelledStamp from "../images/cancelled_stamp.svg"
import { PERMISSIONS, useCheckPermissions } from "../Auth"

export function HotelOtaBooking({ bookingId }: { bookingId: number }) {
	const { booking, isLoading, error, refresh } = useHotelOtaBooking(bookingId)
	const navigateToBack = useNavigateToBack("/org/ota-bookings")
	const { hasPermission } = useCheckPermissions()
	const canRetry = hasPermission(PERMISSIONS.MODIFY_HOTEL_BOOKINGS)

	if (isLoading) {
		return (
			<Box padding="6" textAlign="center">
				<Spinner />
			</Box>
		)
	}

	if (error || !booking) {
		return (
			<Box padding="6" textAlign="center">
				<Text color="danger">Failed to load the booking.</Text>
			</Box>
		)
	}

	return (
		<Stack gap="4">
			<Inline
				gap="3"
				alignItems="center"
				paddingY="3"
				paddingX="6"
				borderBottomWidth="1"
			>
				<Box
					cursor="pointer"
					display="flex"
					alignItems="center"
					onClick={() => navigateToBack()}
					title="Back to OTA Bookings"
				>
					<Icons.ArrowLeft size="5" />
				</Box>
				<Heading as="h3">Booking #{booking.display_id || booking.id}</Heading>
				<OtaBookingVersionsInDialog
					bookingId={booking.id}
					hotel={booking.hotel}
					isBooked={booking.latest_stage.state === "booked"}
				>
					{({ open }) => {
						const hasMultipleVersions = booking.latest_booking.version > 1

						return hasMultipleVersions ? (
							<Box cursor="pointer" onClick={open}>
								<Badge borderWidth="0">v{booking.latest_booking.version}</Badge>
							</Box>
						) : null
					}}
				</OtaBookingVersionsInDialog>
			</Inline>
			{booking.remote_booking_is_synced ? null : (
				<Box paddingX="6">
					<Inline
						color="danger"
						paddingX="4"
						paddingY="3"
						rounded="lg"
						borderColor="danger_emphasis"
						borderWidth="1"
						bgColor="danger"
						justifyContent="between"
					>
						<Stack>
							<Inline alignItems="center" gap="2">
								<Icons.CircleAlert />
								<Text fontWeight="semibold">
									Failed to push{" "}
									{booking.latest_stage.state === "booked"
										? "booking"
										: "cancellation"}{" "}
									to hotel
								</Text>
							</Inline>
							<Text>
								{booking.remote_booking_sync_attempted_at ? (
									<>
										Last attempt at:{" "}
										<Time
											timestamp={booking.remote_booking_sync_attempted_at}
											dateFormat="D MMM YYYY [•] h:mm A"
										/>{" "}
										• v{booking.latest_booking.version}&nbsp;|&nbsp;
									</>
								) : null}
								{booking.remote_synced_booking
									? `Current booking status at hotel - v${booking.remote_synced_booking.version}`
									: "Booking never sent to Hotel"}
							</Text>
						</Stack>
						{canRetry ? (
							<Inline alignItems="center">
								<RetryOtaBookingSync
									booking={booking}
									onChange={() => refresh()}
								/>
							</Inline>
						) : null}
					</Inline>
				</Box>
			)}
			<Stack gap="4" paddingX="6" paddingBottom="6">
				<OtaBookingSummary booking={booking} />
				<Inline gap="4" alignItems="start" flexWrap="wrap">
					<Stack gap="4" flex="1">
						<OtaBookingRooms rooms={booking.latest_booking.rooms} />
						{booking.latest_booking.extra_services.length ? (
							<OtaBookingExtraServices
								extraServices={booking.latest_booking.extra_services}
							/>
						) : null}
					</Stack>
					<Box flex="1" maxWidth="lg" style={{ minWidth: "15rem" }}>
						<OtaBookingPayments booking={booking} />
					</Box>
				</Inline>
			</Stack>
		</Stack>
	)
}

function OtaBookingSummary({ booking }: { booking: TOtaBookingDetail }) {
	const {
		latest_stage,
		latest_booking,
		stages,
		is_b2b: isB2B,
		remote_booking_is_synced,
		trip,
		trip_source,
		trip_source_contact,
		booking_confirmation_number,
	} = booking
	const stage = latest_stage.state
	const isFullyPaid = isBookingFullyPaid(booking)
	const guest = {
		name: latest_booking.guest_name,
		phone: latest_booking.guest_phone_number,
		email: latest_booking.guest_email,
	}
	// The stages are newest first, so the booking was made at the last booked one
	const bookedStage = stages.filter((stage) => stage.state === "booked").pop()
	return (
		<Stack padding="6" rounded="md" borderWidth="1" bgColor="default">
			<Inline
				gap="4"
				justifyContent="between"
				alignItems="start"
				flexWrap="wrap"
			>
				<Stack gap="2">
					<Inline gap="1" alignItems="center">
						<Badge
							status={stage === "booked" ? "success" : undefined}
							borderWidth="0"
						>
							{stage === "booked" ? "Booked" : "Cancelled"}
						</Badge>
						{remote_booking_is_synced && booking.remote_booking_synced_at ? (
							<Tooltip
								content={
									<Stack>
										<Text>Booking pushed to hotel on </Text>
										<Time
											timestamp={booking.remote_booking_synced_at}
											dateFormat="D MMM YYYY [•] h:mm A"
										/>
									</Stack>
								}
							>
								<Box color="success" title="Synced">
									<Icons.Ok />
								</Box>
							</Tooltip>
						) : null}
						<Text color="subtle">•</Text>
						<Badge
							status={
								stage === "booked" ? (isB2B ? "purple" : "primary") : undefined
							}
							size="sm"
							borderWidth="0"
						>
							{isB2B ? "B2B" : "Guest"}
						</Badge>
						{isFullyPaid ? (
							<>
								<Text color="subtle">•</Text>
								<Badge status="success" size="sm" borderWidth="0">
									<Icons.OkCircleSolid /> Fully Paid
								</Badge>
							</>
						) : null}
					</Inline>
					<Inline gap="2" alignItems="center">
						{isB2B ? (
							<Icons.Briefcase size="6" />
						) : (
							<Icons.UserRound size="6" />
						)}
						<Heading as="h2">
							{isB2B && trip_source ? trip_source.name : guest.name}
						</Heading>
					</Inline>
					<OtaBookingContact
						contact={
							isB2B && trip_source_contact
								? {
										name: trip_source_contact.name,
										phone: trip_source_contact.phone_number || null,
										email: trip_source_contact.email || null,
									}
								: guest
						}
						isPerson={isB2B}
					/>
					{isB2B && guest.phone ? (
						<Inline
							gap="2"
							alignItems="center"
							padding="2"
							rounded="md"
							bgColor="primary"
							fontSize="sm"
						>
							<Text color="accent" fontWeight="semibold">
								Booked for
							</Text>
							<Icons.UserRound />
							<Text fontWeight="semibold">{guest.name}</Text>
							<Text color="subtle">•</Text>
							<OtaBookingContact contact={guest} />
						</Inline>
					) : null}
				</Stack>
				<Inline gap="3" flexWrap="wrap">
					{trip ? (
						<OtaBookingFact label="Trip ID" bgColor="primary">
							<Inline gap="2">
								<Text fontSize="md" fontWeight="semibold">
									{trip.id}
								</Text>
								<Link
									to={generatePath("/trips/:tripId", {
										tripId: String(trip.id),
									})}
									color="primary"
								>
									<Icons.ArrowTopRightOnSquare style={{ strokeWidth: 2 }} />
								</Link>
							</Inline>
						</OtaBookingFact>
					) : null}
					{stage === "cancelled" ? (
						<OtaBookingFact label="Cancelled On" bgColor="inset">
							<Inline gap="1" alignItems="center">
								<Icons.CalendarDays color="muted" />
								<Time
									timestamp={latest_stage.created_at}
									dateFormat
									fontSize="md"
									fontWeight="semibold"
								/>
							</Inline>
						</OtaBookingFact>
					) : bookedStage ? (
						<OtaBookingFact label="Booked On" bgColor="success">
							<Inline gap="1" alignItems="center">
								<Icons.CalendarDays color="success" />
								<Time
									timestamp={bookedStage.created_at}
									dateFormat
									fontSize="md"
									fontWeight="semibold"
								/>
							</Inline>
						</OtaBookingFact>
					) : null}
					{booking_confirmation_number ? (
						<OtaBookingFact
							label="Confirmation ID"
							bgColor={stage === "booked" ? "purple" : "inset"}
						>
							<Inline gap="2" alignItems="center">
								<Icons.Ticket
									color={stage === "booked" ? "purple" : "muted"}
									style={{ strokeWidth: 2 }}
								/>
								<Text fontSize="lg">{booking_confirmation_number}</Text>
								<CopyToClipboard>
									{({ copyTextToClipboard }) => (
										<CopyToClipboardButton
											onClick={() =>
												copyTextToClipboard(booking_confirmation_number)
											}
											level="tertiary"
											size="sm"
										>
											<Icons.CopyCheck />
										</CopyToClipboardButton>
									)}
								</CopyToClipboard>
							</Inline>
						</OtaBookingFact>
					) : null}
				</Inline>
			</Inline>
			<Divider marginY="3" />
			<Inline justifyContent="between">
				<OtaBookingStay
					hotel={booking.hotel}
					version={latest_booking}
					isBooked={stage === "booked"}
				/>
				<OtaBookingStamp
					status={
						stage === "booked" && isFullyPaid
							? "paid"
							: stage === "cancelled"
								? "cancelled"
								: undefined
					}
				/>
			</Inline>
		</Stack>
	)
}

function OtaBookingContact({
	contact,
	isPerson = false,
}: {
	contact: { name: string; phone: string | null; email: string | null }
	isPerson?: boolean
}) {
	return (
		<Inline
			gap="2"
			alignItems="center"
			color="muted"
			flexWrap="wrap"
			fontSize="sm"
		>
			{isPerson ? (
				<>
					<Inline gap="1" alignItems="center">
						<Icons.Identification />
						<Text>{contact.name}</Text>
					</Inline>
					<Text color="subtle">•</Text>
				</>
			) : null}
			{contact.phone ? (
				<Inline gap="1" alignItems="center">
					<Icons.Phone />
					<Text>{contact.phone}</Text>
				</Inline>
			) : null}
			{contact.phone && contact.email ? <Text color="subtle">•</Text> : null}
			{contact.email ? (
				<Inline gap="1" alignItems="center">
					<Icons.Mail />
					<Text>{contact.email}</Text>
				</Inline>
			) : null}
		</Inline>
	)
}

function OtaBookingFact({
	label,
	children,
	bgColor = "subtle",
}: {
	label?: string
	children: React.ReactNode
	bgColor?: BoxOwnProps["bgColor"]
}) {
	return (
		<Stack gap="1" padding="3" rounded="lg" bgColor={bgColor}>
			{label ? (
				<Text
					fontSize="xs"
					color="muted"
					textTransform="uppercase"
					fontWeight="semibold"
				>
					{label}
				</Text>
			) : null}
			{children}
		</Stack>
	)
}

// The "Cancelled" / "Paid in full" mark on the summary
function OtaBookingStamp({
	status,
}: {
	status: "cancelled" | "paid" | undefined
}) {
	if (!status) return null
	return (
		<Inline justifyContent="end">
			<Box
				as="img"
				src={status === "cancelled" ? cancelledStamp : paidStamp}
				alt={status === "cancelled" ? "Cancelled" : "Fully Paid"}
				loading="lazy"
				height="auto"
				width="auto"
			/>
		</Inline>
	)
}

function OtaBookingStay({
	hotel,
	version,
	isBooked,
	showTotalAmount = false,
}: {
	hotel: TOtaBookingDetail["hotel"]
	version: TOtaBookingVersionWithRooms
	isBooked: boolean
	showTotalAmount?: boolean
}) {
	const {
		no_of_adults,
		children,
		no_of_rooms,
		no_of_extra_services,
		checkin_date,
		checkin_date_local,
		checkout_date,
		checkout_date_local,
		total_amount_money,
	} = version
	const nights = getDiff(
		localOrUtcTimestampToLocalDate(checkout_date_local, checkout_date),
		localOrUtcTimestampToLocalDate(checkin_date_local, checkin_date),
		"days"
	)
	return (
		<Inline gap="4" alignItems="start" flexWrap="wrap">
			<Box
				as="img"
				src={hotel.image_url}
				alt={hotel.name}
				loading="lazy"
				rounded="md"
				style={{ width: 160, height: 120, objectFit: "cover" }}
			/>
			<Stack gap="2">
				<Inline gap="12" justifyContent="between">
					<Stack>
						<Text fontSize="lg" fontWeight="semibold">
							{hotel.name}
						</Text>
						{hotel.location.name ? (
							<Inline gap="1" alignItems="center" color="muted" fontSize="sm">
								<Icons.LocationMarker />
								<Text>{hotel.location.name}</Text>
							</Inline>
						) : null}
					</Stack>
					{showTotalAmount ? (
						<OtaBookingFact
							label="Total Amount"
							bgColor={isBooked ? "purple" : "inset"}
						>
							<Money
								money={total_amount_money}
								showCurrency
								fontSize="md"
								fontWeight="semibold"
							/>
						</OtaBookingFact>
					) : null}
				</Inline>
				<Inline gap="4" flexWrap="wrap">
					<OtaBookingFact bgColor={isBooked ? "primary" : "inset"}>
						<Inline gap="8">
							<Stack gap="1">
								<Text
									textTransform="uppercase"
									fontSize="xs"
									fontWeight="semibold"
									color="muted"
								>
									Check In
								</Text>
								<Inline gap="2" alignItems="center">
									<Icons.CalendarDays color="muted" />
									<Time
										timestamp={checkin_date}
										localTimestamp={checkin_date_local}
										dateFormat
										fontSize="md"
										fontWeight="semibold"
									/>
								</Inline>
							</Stack>
							<Box alignSelf="center">
								<Badge status={isBooked ? "primary" : undefined} fullRounded>
									<Icons.Moon /> {nights} Nights
								</Badge>
							</Box>
							<Stack gap="1">
								<Text
									textTransform="uppercase"
									fontSize="xs"
									fontWeight="semibold"
									color="muted"
								>
									Check Out
								</Text>
								<Inline gap="2" alignItems="center">
									<Icons.CalendarDays color="muted" />
									<Time
										timestamp={checkout_date}
										localTimestamp={checkout_date_local}
										dateFormat
										fontSize="md"
										fontWeight="semibold"
									/>
								</Inline>
							</Stack>
						</Inline>
					</OtaBookingFact>
					<OtaBookingFact label="Guest" bgColor={isBooked ? "accent" : "inset"}>
						<Inline gap="3" alignItems="center">
							<Inline
								gap="1"
								alignItems="center"
								title="Adults"
								fontWeight="semibold"
								fontSize="md"
							>
								<Icons.Users2 color={isBooked ? "accent" : "muted"} />{" "}
								{no_of_adults}
							</Inline>
							<OtaBookingChildrenCount
								guestChildren={children}
								iconColor={isBooked ? "accent" : "muted"}
								fontWeight="semibold"
								fontSize="md"
							/>
						</Inline>
					</OtaBookingFact>
					<OtaBookingFact label="Rooms" bgColor={isBooked ? "accent" : "inset"}>
						<Inline
							gap="1"
							alignItems="center"
							fontWeight="semibold"
							fontSize="md"
						>
							<Icons.BedDouble color={isBooked ? "accent" : "muted"} />{" "}
							{no_of_rooms}
						</Inline>
					</OtaBookingFact>
					{no_of_extra_services ? (
						<OtaBookingFact
							label="Extra Services"
							bgColor={isBooked ? "accent" : "inset"}
						>
							<Inline
								gap="1"
								alignItems="center"
								fontWeight="semibold"
								fontSize="md"
							>
								<Icons.DiamondPlus color={isBooked ? "accent" : "muted"} />{" "}
								{no_of_extra_services}
							</Inline>
						</OtaBookingFact>
					) : null}
				</Inline>
			</Stack>
		</Inline>
	)
}

function OtaBookingSection({
	icon,
	title,
	children,
}: {
	icon: React.ReactNode
	title: string
	children: React.ReactNode
}) {
	return (
		<Stack gap="2">
			<Inline gap="2" alignItems="center" color="primary" paddingLeft="4">
				{icon}
				<Text fontWeight="semibold" color="default">
					{title}
				</Text>
			</Inline>
			{children}
		</Stack>
	)
}

function OtaBookingImage({
	url,
	alt,
}: {
	url: string | null | undefined
	alt: string
}) {
	if (!url) return null
	return (
		<Box
			as="img"
			src={url}
			alt={alt}
			loading="lazy"
			rounded="md"
			style={{ width: 80, height: 55, objectFit: "cover" }}
		/>
	)
}

function roomOccupancyLabels(room: TOtaBookingRoom) {
	// base_pax counts the adults in the base occupancy only
	const adults = room.base_pax + room.adults_with_extra_bed
	const labels = [
		room.adults_with_extra_bed
			? `${adults}A (${room.adults_with_extra_bed}A with Extra Bed)`
			: `${adults}A`,
	]
	const children = [
		...room.children_with_extra_bed_ages.map((age) => `${age}y with Extra Bed`),
		...room.children_without_extra_bed_ages.map(
			(age) => `${age}y without Extra Bed`
		),
		...room.children_complimentary_ages.map((age) => `${age}y Complimentary`),
	]
	if (children.length) {
		labels.push(`${children.length}C (${children.join(", ")})`)
	}
	return labels
}

function OtaBookingRooms({ rooms }: { rooms: Array<TOtaBookingRoom> }) {
	return (
		<OtaBookingSection icon={<Icons.BedDouble />} title="Rooms">
			<Stack gap="4" padding="4" rounded="md" borderWidth="1" bgColor="default">
				{rooms.map((room) => (
					<Inline
						key={room.room_key}
						gap="4"
						justifyContent="between"
						alignItems="start"
					>
						<Inline gap="4" alignItems="start">
							<OtaBookingImage
								url={room.ota_room_type.image_url}
								alt={room.ota_room_type.name}
							/>
							<Stack gap="1">
								<Text fontWeight="semibold" fontSize="md">
									{room.ota_room_type.name}
								</Text>
								<Inline gap="1" alignItems="center" color="muted" fontSize="sm">
									<Icons.Utensils />
									<Text>{room.ota_meal_plan.name}</Text>
								</Inline>
								{roomOccupancyLabels(room).map((label) => (
									<Text key={label} color="muted" fontSize="sm">
										{label}
									</Text>
								))}
							</Stack>
						</Inline>
						<Stack gap="1" alignItems="end">
							{room.nights.length ? (
								<Tooltip
									content={
										<Stack gap="1">
											<Text fontWeight="semibold">Room Rate</Text>
											<Inline justifyContent="between" gap="10" color="muted">
												<Text>Date</Text>
												<Text>Price</Text>
											</Inline>
											<Divider marginY="0" />
											{room.nights.map((night) => (
												<Inline
													key={night.date}
													justifyContent="between"
													gap="10"
												>
													<Text>
														{formatDate(
															localOrUtcTimestampToLocalDate(
																night.date_local,
																night.date
															),
															"D MMM"
														)}
													</Text>
													<Money money={night.amount_money} />
												</Inline>
											))}
											<Divider marginY="0" />
											<Inline justifyContent="between" gap="10">
												<Text>Taxes</Text>
												<Money money={room.tax_money} />
											</Inline>
										</Stack>
									}
								>
									<Box>
										<Money
											money={room.amount_money}
											showCurrency
											fontWeight="semibold"
										/>
									</Box>
								</Tooltip>
							) : (
								<Money
									money={room.amount_money}
									showCurrency
									fontWeight="semibold"
								/>
							)}
							<Text color="muted" fontSize="sm">
								for {room.no_of_nights} Nights
							</Text>
						</Stack>
					</Inline>
				))}
			</Stack>
		</OtaBookingSection>
	)
}

function OtaBookingExtraServices({
	extraServices,
}: {
	extraServices: Array<TOtaBookingExtraService>
}) {
	return (
		<OtaBookingSection icon={<Icons.DiamondPlus />} title="Extra Services">
			<Stack gap="4" padding="4" rounded="md" borderWidth="1" bgColor="default">
				{extraServices.map((extraService) => (
					<Inline
						key={extraService.id}
						gap="4"
						justifyContent="between"
						alignItems="start"
					>
						<Inline gap="4" alignItems="start">
							<OtaBookingImage
								url={extraService.ota_addon.image_url}
								alt={extraService.ota_addon.name}
							/>
							<Stack gap="1">
								<Text fontWeight="semibold" fontSize="md">
									{extraService.ota_addon.name}
								</Text>
								{extraService.ota_addon.description ? (
									<Text color="muted" fontSize="sm">
										{extraService.ota_addon.description}
									</Text>
								) : null}
								<Inline gap="2" alignItems="center">
									<Badge size="sm" status="primary">
										<Inline alignItems="center" gap="1">
											<Icons.CalendarDays />{" "}
											<Time
												timestamp={extraService.date}
												localTimestamp={extraService.date_local}
												format="ddd, D MMM"
											/>
										</Inline>
									</Badge>
									{extraService.ota_addon.is_service_date_flexible ? (
										<Inline gap="1" alignItems="center" color="muted">
											<Icons.Info />
											<i>Subject to availability</i>
										</Inline>
									) : null}
								</Inline>
							</Stack>
						</Inline>
						<Stack gap="1" alignItems="end">
							<Tooltip
								content={
									<Stack gap="1">
										<Text fontWeight="semibold">
											{extraService.ota_addon.name} Rate
										</Text>
										<Inline justifyContent="between" gap="10" color="muted">
											<Text>Date</Text>
											<Text>Price</Text>
										</Inline>
										<Divider marginY="0" />
										<Inline
											key={extraService.date}
											justifyContent="between"
											gap="10"
										>
											<Text>
												{formatDate(
													localOrUtcTimestampToLocalDate(
														extraService.date_local,
														extraService.date
													),
													"D MMM"
												)}
											</Text>
											<Money money={extraService.amount_money} />
										</Inline>
										<Divider marginY="0" />
										<Inline justifyContent="between" gap="10">
											<Text>Taxes</Text>
											<Money money={extraService.tax_money} />
										</Inline>
									</Stack>
								}
							>
								<Box>
									<Money
										money={extraService.amount_money}
										showCurrency
										fontWeight="semibold"
									/>
								</Box>
							</Tooltip>
							<Text color="muted" fontSize="sm">
								for {extraService.quantity}{" "}
								{extraService.quantity === 1 ? "Item" : "Items"}
							</Text>
						</Stack>
					</Inline>
				))}
			</Stack>
		</OtaBookingSection>
	)
}

function OtaBookingVersionsInDialog({
	bookingId,
	hotel,
	isBooked,
	children,
}: {
	bookingId: number
	hotel: TOtaBookingDetail["hotel"]
	isBooked: boolean
	children: (props: { open: () => void }) => React.ReactNode
}) {
	const [isOpen, open, close] = useDialog()
	return (
		<>
			{children({ open })}
			<Dialog open={isOpen} onClose={close} title="Versions" xl>
				<Dialog.Body paddingY="0" paddingX="0">
					<OtaBookingVersions
						bookingId={bookingId}
						hotel={hotel}
						isBooked={isBooked}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function OtaBookingVersions({
	bookingId,
	hotel,
	isBooked,
}: {
	bookingId: number
	hotel: TOtaBookingDetail["hotel"]
	isBooked: boolean
}) {
	const { versions, isLoading, error } = useHotelOtaBookingVersions(bookingId)
	const [selectedVersionId, setSelectedVersionId] = useState<number>()
	const { xs, sm } = useBreakpoints()

	if (isLoading) {
		return (
			<Box padding="6" textAlign="center">
				<Spinner />
			</Box>
		)
	}
	if (error || !versions) {
		return (
			<Box padding="6">
				<Alert status="error">Failed to load the versions.</Alert>
			</Box>
		)
	}
	// The versions come newest first, so the first one is the current one
	const selectedVersion =
		versions.find((version) => version.id === selectedVersionId) || versions[0]
	if (!selectedVersion) {
		return (
			<Box padding="6">
				<Text color="muted">No versions found.</Text>
			</Box>
		)
	}
	const selectedVersionContent = (
		<Stack gap="4" padding="4" bgColor="inset">
			<OtaBookingVersionSummary
				hotel={hotel}
				version={selectedVersion}
				isBooked={isBooked}
			/>
			<OtaBookingRooms rooms={selectedVersion.rooms} />
			{selectedVersion.extra_services.length ? (
				<OtaBookingExtraServices
					extraServices={selectedVersion.extra_services}
				/>
			) : null}
		</Stack>
	)

	if (xs || sm) {
		const versionOptions = versions.map((version) => ({
			id: version.id,
			name: `v${version.version}`,
			isLatest: version.is_latest,
		}))
		return (
			<Stack gap="0">
				<Box padding="4">
					<Select
						value={versionOptions.find(
							(option) => option.id === selectedVersion.id
						)}
						onChange={(option: (typeof versionOptions)[number]) => {
							if (option) setSelectedVersionId(option.id)
						}}
						clearable={false}
						options={versionOptions}
						optionRenderer={({ option }) => (
							<Text>
								{option.name}
								{option.isLatest ? " (Current)" : ""}
							</Text>
						)}
					/>
				</Box>
				{selectedVersionContent}
			</Stack>
		)
	}

	return (
		<Tabs layout="row" collapseBelowXs={false} style={{ minHeight: "60vh" }}>
			<TabsList
				style={{ minWidth: "12rem", maxWidth: "14rem", padding: 0, rowGap: 0 }}
				flexShrink="0"
			>
				<Box padding="6" paddingBottom="2">
					<Text fontWeight="semibold" color="muted" fontSize="sm">
						Versions
					</Text>
				</Box>
				{versions.map((version) => {
					const isActive = version.id === selectedVersion.id
					return (
						<TabItem
							key={version.id}
							active={isActive}
							onClick={() => setSelectedVersionId(version.id)}
						>
							<Stack gap="1">
								<Inline gap="2" alignItems="center">
									<Text
										fontWeight="semibold"
										color={isActive ? "primary" : undefined}
									>
										v{version.version}
									</Text>
									{version.is_latest ? <Badge accent>Current</Badge> : null}
								</Inline>
								<Time
									timestamp={version.created_at}
									dateFormat="D MMM YYYY, HH:mm"
									fontSize="xs"
									color="muted"
									fontWeight="normal"
								/>
							</Stack>
						</TabItem>
					)
				})}
			</TabsList>
			<TabContent active flex="1" minWidth="0" overflow="auto">
				{selectedVersionContent}
			</TabContent>
		</Tabs>
	)
}

function OtaBookingVersionSummary({
	hotel,
	version,
	isBooked,
}: {
	hotel: TOtaBookingDetail["hotel"]
	version: TOtaBookingVersionWithRooms
	isBooked: boolean
}) {
	return (
		<Stack padding="6" rounded="md" borderWidth="1" bgColor="default">
			<Inline gap="4" justifyContent="between" alignItems="start">
				<Stack gap="2">
					<Inline gap="2" alignItems="center">
						<Icons.UserRound size="6" />
						<Heading as="h2">{version.guest_name}</Heading>
					</Inline>
					<OtaBookingContact
						contact={{
							name: version.guest_name,
							phone: version.guest_phone_number,
							email: version.guest_email,
						}}
					/>
				</Stack>
			</Inline>
			<Divider marginY="3" />
			{/* <Inline justifyContent="between" alignItems="start" gap="4"> */}
			<OtaBookingStay
				hotel={hotel}
				version={version}
				isBooked={isBooked}
				showTotalAmount
			/>
			{/* </Inline> */}
		</Stack>
	)
}

function OtaBookingPayments({ booking }: { booking: TOtaBookingDetail }) {
	const {
		total_amount_money: total,
		paid_amount_money: paid,
		due_amount_money: due,
		payments,
	} = booking
	// The amounts come as strings
	const totalAmount = Number(total.amount)
	return (
		<OtaBookingSection icon={<Icons.Receipt />} title="Payments">
			<Stack gap="3" padding="4" rounded="lg" borderWidth="1" bgColor="default">
				<Text fontSize="md">
					Total: <Money money={total} showCurrency fontWeight="semibold" />
				</Text>
				<Box bgColor="inset" rounded="full" style={{ overflow: "hidden" }}>
					<Box
						rounded="full"
						height="2"
						bgColor="success_emphasis"
						style={{
							width: `${totalAmount ? Math.min((Number(paid.amount) / totalAmount) * 100, 100) : 0}%`,
						}}
					/>
				</Box>
				<Inline justifyContent="between">
					<Text color="success" fontWeight="semibold" fontSize="sm">
						Paid: <Money money={paid} showCurrency />
					</Text>
					{Number(due.amount) > 0 ? (
						<Text color="warning" fontWeight="semibold" fontSize="sm">
							Due: <Money money={due} showCurrency />
						</Text>
					) : null}
				</Inline>
				{payments.length ? (
					<OtaBookingPaymentsTable payments={payments} />
				) : null}
			</Stack>
		</OtaBookingSection>
	)
}

function OtaBookingPaymentsTable({
	payments,
}: {
	payments: Array<TOtaBookingPayment>
}) {
	return (
		<Table
			responsive
			fontSize="sm"
			headers={["Received on", "Method", "Amount"]}
			alignCols={{ 2: "right" }}
			rowKeys={payments.map((payment) => payment.id)}
			rows={payments.map((payment) => {
				const reference = payment.reference
				return [
					<Stack gap="1">
						<Inline gap="1" alignItems="center" color="muted">
							<Icons.CalendarDays />
							<Time timestamp={payment.paid_at} dateFormat />
						</Inline>
						<Inline gap="1" alignItems="center" color="muted">
							<Icons.Clock />
							<Time timestamp={payment.paid_at} timeFormat />
						</Inline>
					</Stack>,
					<Stack gap="1">
						{payment.method ? (
							<Text fontWeight="semibold">
								{payment.method.charAt(0).toUpperCase() +
									payment.method.slice(1).replace(/_/g, " ")}
							</Text>
						) : (
							"N/A"
						)}
						{reference ? (
							<Inline alignItems="center" gap="1">
								<Text color="muted">{reference}</Text>
								<CopyToClipboard>
									{({ copyTextToClipboard }) => (
										<CopyToClipboardButton
											onClick={() => copyTextToClipboard(reference)}
											level="tertiary"
											size="sm"
										>
											<Icons.CopyCheck />
										</CopyToClipboardButton>
									)}
								</CopyToClipboard>
							</Inline>
						) : null}
					</Stack>,
					<Stack gap="1" alignItems="end">
						<Money money={payment.amount_money} showCurrency />
					</Stack>,
				]
			})}
		/>
	)
}

// Nothing is left to pay on a booking that has been paid for
function isBookingFullyPaid(booking: TOtaBookingDetail) {
	return (
		Number(booking.due_amount_money.amount) === 0 &&
		Number(booking.paid_amount_money.amount) > 0
	)
}
